import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import CurrentProfileContext from './CurrentProfile.context';
import { Loader } from 'ncoded-component-library';
import api from 'api';
import { User } from 'models/User';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext, {
  CallbackConfig,
} from 'providers/CurrentUser/CurrentUser.context';
import utils from 'utils';
import { SUCCESS_MESSAGES } from 'request-responses';
import { DropzoneFile } from 'components/Dropzone/utils';
import { multiSelectKeys } from 'providers/CurrentUser/CurrentUser.provider';

const PATCH_NUM_KEYS = [
  'ebitda',
  'revenueMin',
  'ebitdaMin',
  'ebitdaMax',
  'budgetMinPrice',
  'budgetMaxPrice',
];

const CurrentProfileProvider: React.FC = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentProfile, setCurrentProfile] = useState<User | null>();

  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { id: profileId } = useParams();

  const isMyProfile = useMemo(
    () => currentProfile?._id === currentUser?._id,
    [currentProfile, currentUser],
  );

  const { t } = useTranslation();

  const profileFormValues = useMemo(() => {
    if (!currentProfile) return null;

    return currentProfile;
  }, [currentProfile]);

  const updateUser = useCallback(
    (payload: Partial<User>) => {
      setCurrentProfile((old) => ({ ...old, ...payload }));

      if (isMyProfile) {
        setCurrentUser((old) => ({ ...old, ...payload }));
      }
    },
    [isMyProfile, setCurrentUser],
  );

  useEffect(() => {
    if (!profileId) {
      setCurrentProfile(currentUser);
      return;
    }

    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { user },
        } = await api.users.getUser(profileId);
        setCurrentProfile(user);
      } catch (e) {
        popServerError(e, t('previousSessionExpired'));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [currentUser, profileId, popServerError, t]);

  const patchCurrentProfile = useCallback(
    async (
      value: Partial<User>,
      { skipToast, onSuccess, onError }: CallbackConfig = {
        skipToast: false,
        onSuccess: utils.noop,
        onError: utils.noop,
      },
    ) => {
      if (!currentUser) return;

      const patchedValues = (
        Object.entries(value) as Array<[keyof User, User[keyof User]]>
      ).reduce((acc, [key, value]) => {
        let patchVal;
        if (multiSelectKeys.includes(key)) {
          patchVal = utils.multiSelectValueToStringArray(
            value as Array<Record<string, any> & { value: string }>,
          );
        } else if (PATCH_NUM_KEYS.includes(key)) {
          patchVal = utils.getIntegerFromDottedNumber(value as string);
        } else {
          patchVal = value;
        }
        return { ...acc, [key]: patchVal };
      }, {} as Partial<User>);

      try {
        const {
          data: { user },
        } = await api.users.patchUser(currentProfile._id, patchedValues);
        updateUser(user);
        onSuccess?.();

        if (!skipToast) popSuccess(t(SUCCESS_MESSAGES.PACHED_USER));
      } catch (e) {
        popServerError(e);
        onError?.();
      }
    },
    [
      currentProfile?._id,
      currentUser,
      popSuccess,
      popServerError,
      t,
      updateUser,
    ],
  );

  const value = useMemo(
    () => ({
      isMyProfile,
      currentProfile,
      profileFormValues,
      patchCurrentProfile,
      updateProfilePicture: (profileImage: DropzoneFile | null) =>
        updateUser({ profileImage }),
      updateFirmLogo: (firmLogo: DropzoneFile | null) =>
        updateUser({ firmLogo }),
    }),
    [
      isMyProfile,
      currentProfile,
      profileFormValues,
      patchCurrentProfile,
      updateUser,
    ],
  );

  if (isLoading || !currentProfile)
    return (
      <div style={{ position: 'relative', minHeight: 100 }}>
        <Loader overlay={false} />
      </div>
    );

  return (
    <CurrentProfileContext.Provider value={value}>
      {children}
    </CurrentProfileContext.Provider>
  );
};
export default CurrentProfileProvider;
